import React from 'react';
import { BookOpen } from 'lucide-react';

export default function KnowledgeBaseSection({ config, onChange }) {
  const handleChange = (field, value) => {
    onChange({
      ...config,
      [field]: value
    });
  };

  // Default comprehensive content for ADT Express
  const defaultServices = `ADT Express est spécialisé dans le transport maritime entre la France et la Côte d'Ivoire. Nos services incluent: • Transport Maritime International: Expéditions régulières France → Côte d'Ivoire
• Groupage (LCL): Envois de petits volumes partagés dans un conteneur
• Conteneurs Complets (FCL): 20 pieds et 40 pieds pour gros volumes
• Livraison à Domicile: Service porte-à-porte jusqu'à Abidjan et environs
• Collecte en France: Ramassage à domicile dans toute la France
• Dédouanement: Prise en charge complète des formalités douanières
• Assurance Cargo: Protection de vos marchandises pendant le transport
• Suivi en Temps Réel: Tracking GPS et notifications WhatsApp
• Stockage Temporaire: Entreposage sécurisé avant expédition`;

  const defaultProcess = `Processus d'Expédition ADT Express (France → Côte d'Ivoire):

1. COLLECTE EN FRANCE
   - Prise de rendez-vous pour collecte à domicile
   - Inspection et inventaire des colis
   - Signature du bon de collecte avec photos
   - Transport vers notre entrepôt en France

2. PRÉPARATION ET CONSOLIDATION
   - Réception et vérification des marchandises
   - Emballage professionnel si nécessaire
   - Consolidation dans le conteneur approprié
   - Préparation des documents d'exportation

3. TRANSPORT MARITIME
   - Chargement au port du Havre
   - Navigation transatlantique (durée: 3-4 semaines)
   - Suivi GPS du conteneur en temps réel
   - Notifications automatiques à chaque étape

4. ARRIVÉE À ABIDJAN
   - Déchargement au Port Autonome d'Abidjan
   - Dédouanement (prise en charge par ADT Express)
   - Paiement des droits et taxes de douane
   - Transfert vers notre entrepôt à Abidjan

5. LIVRAISON FINALE
   - Notification au destinataire
   - Planification de la livraison à domicile
   - Livraison par nos chauffeurs avec signature
   - Photos de preuve de livraison

Délai Total: 4-6 semaines de porte à porte`;

  const defaultPolicies = `Politiques et Conditions ADT Express:

REMBOURSEMENT ET ANNULATION:
• Annulation gratuite jusqu'à 48h avant la collecte
• Remboursement à 100% si annulation avant chargement du conteneur
• Remboursement à 50% si annulation après chargement mais avant départ du navire
• Aucun remboursement après départ du navire (possibilité de réexpédition)

GARANTIES ET ASSURANCES:
• Assurance cargo incluse jusqu'à 1000€ par m³
• Assurance complémentaire disponible pour objets de valeur
• Garantie de remplacement en cas de dommage prouvé
• Indemnisation selon les conventions maritimes internationales

RESPONSABILITÉS:
• ADT Express est responsable des marchandises de la collecte à la livraison
• Le client doit déclarer la valeur exacte des biens transportés
• Marchandises interdites: armes, drogues, produits dangereux, contrefaçons
• Inspection douanière: ADT Express n'est pas responsable des saisies légales

PAIEMENT:
• Paiement en France: 50% à la réservation, 50% avant chargement
• Paiement en Côte d'Ivoire: Frais de douane et livraison payables à réception
• Modes de paiement: Virement, carte bancaire, espèces, Mobile Money
• Facture détaillée fournie pour chaque expédition

CONDITIONS GÉNÉRALES:
• Délais indicatifs non contractuels (aléas maritimes possibles)
• Le client doit fournir des documents d'identité valides
• ADT Express se réserve le droit de refuser certaines marchandises
• Litiges régis par le droit français et ivoirien`;

  const defaultSpecialNotes = `Notes Spéciales et Restrictions:

MARCHANDISES ACCEPTÉES:
✓ Effets personnels, vêtements, chaussures
✓ Électroménager (réfrigérateurs, machines à laver, etc.)
✓ Meubles, décoration
✓ Véhicules (voitures, motos) - VOIR PROCÉDURE SPÉCIALE CI-DESSOUS
✓ Denrées alimentaires non périssables
✓ Matériel professionnel et équipements

🚗 PROCÉDURE SPÉCIALE POUR VÉHICULES:
• Les véhicules (voitures, motos) SONT acceptés par ADT Express
• Ils nécessitent un conteneur complet (20 ou 40 pieds selon le véhicule)
• Documents obligatoires: carte grise, certificat de non-gage, facture d'achat
• Dédouanement spécifique en Côte d'Ivoire avec taxes véhicules
• Délai: même que les autres envois (4-6 semaines)
• Le véhicule doit être en bon état de marche
• Vidange et nettoyage complet requis avant expédition

📦 GROS ÉLECTROMÉNAGER (Réfrigérateurs, Congélateurs):
• Acceptés en groupage si dimensions standards (< 2m de hauteur)
• Si très gros modèle (réfrigérateur américain, congélateur industriel), peut nécessiter conteneur complet
• Emballage renforcé obligatoire
• Vidange et nettoyage complet avant expédition

MARCHANDISES INTERDITES:
✗ Armes et munitions
✗ Drogues et substances illicites
✗ Produits dangereux (explosifs, chimiques)
✗ Contrefaçons et produits piratés
✗ Animaux vivants

CONSEILS PRATIQUES:
• Emballez solidement vos colis (cartons renforcés)
• Étiquetez chaque colis avec nom et téléphone
• Faites un inventaire détaillé avec photos
• Déclarez la valeur réelle pour l'assurance
• Gardez les documents importants avec vous

DOCUMENTS REQUIS:
• Passeport ou CNI du client
• Facture proforma ou liste de colisage
• Certificat d'origine pour certains produits
• Carte grise pour les véhicules

FRAIS ADDITIONNELS POSSIBLES:
• Droits de douane (variables selon marchandises)
• Taxe sur la valeur ajoutée (TVA ivoirienne)
• Frais de stockage si retard de récupération
• Livraison en zone éloignée d'Abidjan

CONTACT URGENCE:
• WhatsApp 24/7: +33 X XX XX XX XX
• Email: contact@adt-express.com
• Suivi en ligne: www.adt-express.com/tracking`;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-4">
        <BookOpen className="text-green-600" size={24} />
        <h2 className="text-xl font-semibold text-gray-900">Base de Connaissances Business</h2>
      </div>
      
      <p className="text-gray-600 text-sm mb-6">
        Configurez les informations détaillées sur ADT Express que Fatou utilisera pour répondre aux questions complexes.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Services Offerts
          </label>
          <textarea
            value={config?.services || defaultServices}
            onChange={(e) => handleChange('services', e?.target?.value)}
            placeholder={"Décrivez tous les services offerts par ADT Express (transport maritime, groupage, conteneurs complets, livraison à domicile, etc.)"}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Fatou utilisera ces informations pour expliquer vos services aux clients</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Processus d'Expédition
          </label>
          <textarea
            value={config?.process || defaultProcess}
            onChange={(e) => handleChange('process', e?.target?.value)}
            placeholder="Expliquez le processus complet: collecte, transport, navigation, douanes, livraison..."
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Détaillez chaque étape du processus d'expédition</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Politiques et Conditions
          </label>
          <textarea
            value={config?.policies || defaultPolicies}
            onChange={(e) => handleChange('policies', e?.target?.value)}
            placeholder="Politiques de remboursement, conditions d'annulation, garanties, assurances, etc."
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Incluez toutes les politiques importantes que les clients doivent connaître</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes Spéciales
          </label>
          <textarea
            value={config?.specialNotes || defaultSpecialNotes}
            onChange={(e) => handleChange('specialNotes', e?.target?.value)}
            placeholder="Informations supplémentaires, restrictions, conseils pour les clients, etc."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Ajoutez toute information complémentaire utile</p>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-purple-800">
          <strong>💡 Astuce:</strong> Plus vous fournissez d'informations détaillées, plus Fatou sera capable de répondre précisément aux questions des clients. Mettez à jour régulièrement ces informations.
        </p>
      </div>
    </div>
  );
}